/**
 * Active Context Types for Phase 7: Routing Simplification
 *
 * Phase 4: Types defined but NOT implemented yet
 * Purpose: Define shape of context-based routing system (to be implemented in Phase 7)
 *
 * Migration Context:
 * - Phase 4-6: Types exist but unused (preparation)
 * - Phase 7: Active Context system fully implemented
 * - Phase 8+: Replaces account_type-based routing entirely
 *
 * Active Context Philosophy:
 * Instead of routing based on account_type (identity-based), route based on
 * which "context" or "perspective" the user is currently working in.
 *
 * @see /unifiedIdentityMigration.md (Phase 7 section)
 */

/**
 * Scope of the active context
 * - personal: Individual user perspective (default for all users)
 * - organization: User is operating in an org context (employee or org admin)
 */
export type ContextScope = 'personal' | 'organization'

/**
 * Active Context state
 *
 * Persisted in sessionStorage to survive page refreshes within a session
 * but resets on browser close (forces re-determination of default context)
 */
export interface ActiveContext {
  /** Unique identifier for this context (for comparison) */
  id: string

  /** Which perspective/context the user is currently operating in */
  scope: ContextScope

  /** Display name for UI (e.g., "Personal", "Smith Family", "ABC Organization") */
  displayName: string

  /** ID of the household or organization (null for personal scope) */
  scopeId: string | null

  /** Whether user is owner/admin of this context */
  isOwner?: boolean
  isAdmin?: boolean
}

/**
 * Default context determination priority (for Phase 7 implementation)
 *
 * When user logs in, determine their default context using this priority:
 * 1. Persisted context (if still available)
 * 2. Default → personal context
 *
 * Organization membership (with or without a plan) populates availableContexts.
 * /org/* routes auto-switch into organization context when needed.
 *
 * Users can manually switch contexts via UI context switcher
 */
export type ContextPriority = 'organization' | 'personal'

/**
 * Helper type for context switcher UI (Phase 7)
 *
 * Lists all available contexts for a user to switch between
 */
export interface AvailableContexts {
  personal: ActiveContext
  organizations?: ActiveContext[]
}

// Helper functions to create context objects
export function createPersonalContext(accountId: string, displayName: string): ActiveContext {
  return {
    id: `personal:${accountId}`,
    scope: 'personal',
    displayName,
    scopeId: null,
  }
}

export function createOrganizationContext(
  orgId: string,
  orgName: string,
  isOwner: boolean,
  isAdmin: boolean,
): ActiveContext {
  return {
    id: `org:${orgId}`,
    scope: 'organization',
    displayName: orgName,
    scopeId: orgId,
    isOwner,
    isAdmin,
  }
}

export interface AvailableContext {
  scope: ContextScope
  scope_id?: string
  display_name: string // e.g., "Personal", "Acme Corp"
  role: string // e.g., "individual", "owner", "admin", "member"
}
